import { useState, useEffect, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { Tile, Wizard, WizardStep, Button } from '@dsi/react-eds';
import ReactEDSWrapper from '../../utils/react-eds-wrapper';
import { useSwobNotifications } from '../../hooks/NotificationsProvider';
import { useTheme } from '../../hooks/useTheme';
import { gnbNodesService } from '../../services/gnbNodesService';
import { gnbDeployService } from '../../services/gnbDeployService';
import LineNumberedEditor from './LineNumberedEditor';

/**
 * Per-node, per-ticket configuration wizard for a CR gNB upgrade.
 * Steps: Overview (read-only) -> CIQ (add/replace/skip) -> Credentials (enter/skip) ->
 * Day-1 (multi-file add/preview/delete) -> Summary.
 *
 * All staging is ticket-scoped (survives refresh/logout): CIQ/Day-1 to minio via the
 * ticket-staging endpoints, credentials encrypted on the gnb_deployment_nodes row. Re-opening
 * reflects saved state (hasStagedCiq/day1 flags, staged Day-1 file list, staged creds username).
 * "Untouched step = keep" — nothing is deleted unless the operator explicitly removes it.
 */

interface NodeLike {
  id: string;
  nodeName: string;
  deploymentId?: string;
  currentRelease?: string;
  currentReleaseVersion?: string;
  currentDraftName?: string;
  referenceId?: string;
  hasStagedCiq?: boolean;
  hasStagedDay1?: boolean;
}

/**
 * Small drag-and-drop file input (or click to browse), using the EDS drag-and-drop visual
 * classes. Self-contained so it can be used multiple times in the wizard (CIQ + Day-1).
 */
function DropZone({ accept, multiple, onFiles, label }: { accept: string; multiple?: boolean; onFiles: (files: File[]) => void; label?: string }) {
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const handle = (files: FileList | null) => {
    if (!files || files.length === 0) return;
    onFiles(Array.from(files));
  };
  return (
    <div
      className="drag-and-drop"
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={(e) => { e.preventDefault(); setDragOver(false); handle(e.dataTransfer.files); }}
      onClick={() => inputRef.current?.click()}
      style={{
        border: `1px dashed ${dragOver ? '#1174e6' : '#7a7a7a'}`,
        borderRadius: 4, padding: '16px', textAlign: 'center', cursor: 'pointer',
        background: dragOver ? 'rgba(17,116,230,0.08)' : 'transparent'
      }}
    >
      <i className="icon icon-upload" style={{ fontSize: 20 }} />
      <p style={{ fontSize: 13, margin: '6px 0 0' }}>
        {label || 'Drag & drop your file here'} or <span style={{ color: '#1174e6' }}>select to browse</span>
      </p>
      <p style={{ fontSize: 11, color: '#8b8b8b', margin: '4px 0 0' }}>Supported: .yaml, .yml</p>
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        multiple={multiple}
        style={{ display: 'none' }}
        onChange={(e) => { handle(e.target.files); e.target.value = ''; }}
      />
    </div>
  );
}

interface NodeConfigWizardProps {
  node: NodeLike;
  ctx: { workflowTicketId: string; ticketId: string; automationFlowId: string };
  onClose: () => void;
  /** called after Save so the parent can refresh row flags */
  onSaved: () => void;
}

export default function NodeConfigWizard({ node, ctx, onClose, onSaved }: NodeConfigWizardProps) {
  const { addNotifications } = useSwobNotifications();
  const { theme } = useTheme();
  const notifyErr = (msg: string) =>
    addNotifications([{ title: 'Error', icon: 'icon-triangle-warning', description: msg, color: 'red', timestamp: new Date() }]);

  // CIQ
  const [ciqStaged, setCiqStaged] = useState<boolean>(!!node.hasStagedCiq);
  const [ciqFileName, setCiqFileName] = useState<string>('');
  const [ciqContent, setCiqContent] = useState<string>('');

  // Credentials
  const [credsSet, setCredsSet] = useState<boolean>(false);
  const [credsUsername, setCredsUsername] = useState<string>('');
  const [newUser, setNewUser] = useState<string>('');
  const [newPass, setNewPass] = useState<string>('');
  const [clearCreds, setClearCreds] = useState<boolean>(false);

  // Day-1 (multi-file)
  const [day1Files, setDay1Files] = useState<string[]>([]);
  const [day1Preview, setDay1Preview] = useState<{ name: string; content: string } | null>(null);

  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);

  const refreshSavedState = useCallback(async () => {
    try {
      const [creds, day1, ciq] = await Promise.all([
        gnbDeployService.getStagedCreds(node.id, ctx.workflowTicketId, ctx.automationFlowId),
        gnbNodesService.tsListDay1(node.id, ctx.ticketId),
        // Per-ticket staged CIQ check — mirrors Day-1 so the staged state is restored on reopen.
        // Resolves to the staged content if present, or null on 404 (nothing staged for this ticket).
        gnbNodesService.tsPreviewCiq(node.id, ctx.ticketId).then((r) => r.data).catch(() => null)
      ]);
      setCredsSet(!!creds.data.hasStagedCreds);
      setCredsUsername(creds.data.username || '');
      setDay1Files(day1.data.files || []);
      setCiqStaged(!!ciq);
    } catch {
      /* first-time: nothing staged yet */
    } finally {
      setLoading(false);
    }
  }, [node.id, ctx.workflowTicketId, ctx.automationFlowId, ctx.ticketId]);

  useEffect(() => {
    refreshSavedState();
  }, [refreshSavedState]);

  // ── CIQ actions ──
  const handleCiqFiles = async (files: File[]) => {
    const file = files[0];
    if (!file) return;
    setCiqFileName(file.name);
    setCiqContent(await file.text());
  };
  const previewCurrentCiq = async () => {
    try {
      const { data } = await gnbNodesService.previewCiq(node.id);
      setDay1Preview({ name: 'Current CIQ', content: data });
    } catch {
      notifyErr('Could not preview current CIQ');
    }
  };
  const downloadCurrentCiq = async () => {
    try {
      const res = await gnbNodesService.downloadCiq(node.id);
      const blob = res.data instanceof Blob ? res.data : new Blob([res.data as string], { type: 'text/yaml' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `${node.nodeName}-ciq.yaml`; a.click();
      URL.revokeObjectURL(url);
    } catch {
      notifyErr('Could not download current CIQ');
    }
  };
  const saveCiq = async () => {
    if (!ciqContent) return;
    await gnbNodesService.tsStageCiq(node.id, ctx, ciqContent);
    setCiqStaged(true);
    setCiqContent('');
    setCiqFileName('');
  };
  const removeCiq = async () => {
    await gnbNodesService.tsDeleteCiq(node.id, ctx);
    setCiqStaged(false);
  };
  const previewCiq = async () => {
    try {
      const { data } = await gnbNodesService.tsPreviewCiq(node.id, ctx.ticketId);
      setDay1Preview({ name: 'CIQ', content: data });
    } catch {
      notifyErr('Could not preview staged CIQ');
    }
  };

  // ── Day-1 actions ──
  const addDay1Files = async (files: File[]) => {
    for (const f of files) {
      if (day1Files.includes(f.name)) {
        notifyErr(`Day-1 file "${f.name}" already added (duplicate names are not allowed)`);
        continue;
      }
      try {
        const content = await f.text();
        await gnbNodesService.tsStageDay1File(node.id, ctx, f.name, content);
      } catch (err) {
        notifyErr(`Failed to add "${f.name}": ${err instanceof Error ? err.message : 'error'}`);
      }
    }
    await refreshSavedState();
  };
  const removeDay1 = async (fileName: string) => {
    await gnbNodesService.tsDeleteDay1File(node.id, ctx, fileName);
    await refreshSavedState();
  };
  const previewDay1 = async (fileName: string) => {
    try {
      const { data } = await gnbNodesService.tsPreviewDay1File(node.id, ctx.ticketId, fileName);
      setDay1Preview({ name: fileName, content: data });
    } catch {
      notifyErr(`Could not preview "${fileName}"`);
    }
  };

  // ── Credentials actions (persisted on save/finish) ──
  const applyCredsChange = async () => {
    if (clearCreds) {
      await gnbDeployService.deleteStagedCreds(node.id, ctx.workflowTicketId, ctx.automationFlowId);
      setCredsSet(false);
      setCredsUsername('');
      setClearCreds(false);
      return;
    }
    if (newUser && newPass) {
      await gnbDeployService.stageCreds({
        gnbNodeId: node.id,
        workflowTicketId: ctx.workflowTicketId,
        ticketId: ctx.ticketId,
        automationFlowId: ctx.automationFlowId,
        username: newUser,
        password: newPass
      });
      setCredsSet(true);
      setCredsUsername(newUser);
      setNewUser('');
      setNewPass('');
    }
  };

  const handleFinish = async () => {
    setSaving(true);
    try {
      if (ciqContent) await saveCiq();
      await applyCredsChange();
      addNotifications([
        { title: 'Saved', icon: 'icon-check', description: `Configuration saved for ${node.nodeName}.`, color: 'green', timestamp: new Date() }
      ]);
      onSaved();
      onClose();
    } catch (err) {
      notifyErr(`Save failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const cell = { padding: '6px 8px' };

  return ReactDOM.createPortal(
    <div style={{ position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.5)' }}>
      <ReactEDSWrapper
        component={Tile}
        title={`Node Configuration — ${node.nodeName}`}
        style={{ width: '80vw', maxWidth: '900px', maxHeight: '90vh', overflow: 'auto' }}
      >
      <ReactEDSWrapper
        component={Wizard}
        onCancel={onClose}
        onFinish={handleFinish}
        cancelBtnLabel="Close"
        finishBtnLabel={saving ? 'Saving…' : 'Save'}
        nextBtnLabel="Next"
        previousBtnLabel="Back"
      >
        {/* Overview (read-only) */}
        <ReactEDSWrapper component={WizardStep} stepTitle="Overview" valid={true}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr><td style={{ ...cell, fontWeight: 600, width: 200 }}>Node</td><td style={cell}>{node.nodeName}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 600 }}>Deployment ID</td><td style={cell}>{node.deploymentId || '—'}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 600 }}>Current release</td><td style={cell}>{node.currentRelease || '—'}{node.currentReleaseVersion ? ` (${node.currentReleaseVersion})` : ''}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 600 }}>Current reference</td><td style={cell}>{node.referenceId || '—'}</td></tr>
            </tbody>
          </table>
          <p style={{ fontSize: 12, color: '#8b8b8b', marginTop: 12 }}>
            Blueprint and target version come from this ticket's onboarding. CIQ, credentials
            and Day-1 are optional for an upgrade — anything you skip is reused from the node's
            previous draft in RLO. Stage a file only to override it for this upgrade.
          </p>
        </ReactEDSWrapper>

        {/* CIQ */}
        <ReactEDSWrapper component={WizardStep} stepTitle="CIQ" valid={true}>
          {/* Current / in-draft CIQ (read-only) — moved here from the row actions menu */}
          {node.currentDraftName && (
            <div style={{ marginBottom: 12, display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 13, fontWeight: 600 }}>Current CIQ:</span>
              <ReactEDSWrapper component={Button} onClick={previewCurrentCiq}>Preview</ReactEDSWrapper>
              <ReactEDSWrapper component={Button} onClick={downloadCurrentCiq}>Download</ReactEDSWrapper>
            </div>
          )}
          {ciqStaged ? (
            <p style={{ fontSize: 13, marginBottom: 12 }}>✅ A CIQ is staged for this node in this ticket.</p>
          ) : (
            <p style={{ fontSize: 13, color: '#8b8b8b', marginBottom: 12 }}>No staged CIQ — the existing CIQ in RLO will be reused. Upload to override (optional).</p>
          )}
          <DropZone accept=".yaml,.yml" onFiles={handleCiqFiles} />
          {ciqFileName && <p style={{ fontSize: 12, marginTop: 8 }}>Selected: {ciqFileName}</p>}
          <div style={{ marginTop: 12, display: 'flex', gap: 8 }}>
            {ciqContent && <ReactEDSWrapper component={Button} primary onClick={saveCiq}>Stage CIQ</ReactEDSWrapper>}
            {ciqStaged && <ReactEDSWrapper component={Button} onClick={previewCiq}>Preview Staged</ReactEDSWrapper>}
            {ciqStaged && <ReactEDSWrapper component={Button} onClick={removeCiq}>Remove Staged</ReactEDSWrapper>}
          </div>
        </ReactEDSWrapper>

        {/* Credentials */}
        <ReactEDSWrapper component={WizardStep} stepTitle="Credentials" valid={true}>
          {credsSet ? (
            <p style={{ fontSize: 13, marginBottom: 12 }}>✅ Credentials set (user: <strong>{credsUsername || '—'}</strong>). Enter new values to overwrite, or leave blank to keep.</p>
          ) : (
            <p style={{ fontSize: 13, color: '#8b8b8b', marginBottom: 12 }}>No staged credentials — stored credentials will be reused. Enter to override (optional).</p>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxWidth: 320 }}>
            <input type="text" placeholder="Username" value={newUser} autoComplete="off" onChange={(e) => setNewUser(e.target.value)} />
            <input type="password" placeholder="Password" value={newPass} autoComplete="new-password" onChange={(e) => setNewPass(e.target.value)} />
            {credsSet && (
              <label style={{ fontSize: 12 }}>
                <input type="checkbox" checked={clearCreds} onChange={(e) => setClearCreds(e.target.checked)} /> Clear saved credentials (reuse existing)
              </label>
            )}
          </div>
        </ReactEDSWrapper>

        {/* Day-1 multi-file */}
        <ReactEDSWrapper component={WizardStep} stepTitle="Day-1 Config" valid={true}>
          <p style={{ fontSize: 12, color: '#8b8b8b', marginBottom: 8 }}>
            Upload only the managed-element files you want to change this upgrade. Files apply in
            filename (alphabetical) order — use a consistent sortable prefix such as
            <code> 01-transport.yaml</code>, <code>02-rru.yaml</code> or <code>A-transport.yaml</code>,
            <code> B-rru.yaml</code>. Avoid unpadded numbers (<code>1, 2, 10</code> sorts as
            <code> 1, 10, 2</code>). Duplicate names are rejected.
          </p>
          <DropZone accept=".yaml,.yml" multiple onFiles={addDay1Files} label="Drag & drop Day-1 files here" />
          <div style={{ marginTop: 12, border: '1px solid #4d4d4d', borderRadius: 4, maxHeight: '30vh', overflow: 'auto' }}>
            {loading ? (
              <p style={{ padding: 8, fontSize: 12, color: '#8b8b8b' }}>Loading…</p>
            ) : day1Files.length === 0 ? (
              <p style={{ padding: 8, fontSize: 12, color: '#8b8b8b' }}>No Day-1 files staged (existing config reused).</p>
            ) : (
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                <tbody>
                  {day1Files.map((f, i) => (
                    <tr key={f} style={{ borderTop: i ? '1px solid #3a3a3a' : 'none' }}>
                      <td style={{ ...cell, width: 32, color: '#8b8b8b' }}>{i + 1}</td>
                      <td style={cell}>{f}</td>
                      <td style={{ ...cell, textAlign: 'right' }}>
                        <ReactEDSWrapper component={Button} onClick={() => previewDay1(f)}>Preview</ReactEDSWrapper>{' '}
                        <ReactEDSWrapper component={Button} onClick={() => removeDay1(f)}>Delete</ReactEDSWrapper>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </ReactEDSWrapper>

        {/* Summary */}
        <ReactEDSWrapper component={WizardStep} stepTitle="Summary" valid={true}>
          <p style={{ fontSize: 13, marginBottom: 12 }}>Review, then Save. Untouched items keep their current state.</p>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <tbody>
              <tr><td style={{ ...cell, fontWeight: 600, width: 160 }}>CIQ</td><td style={cell}>{ciqContent ? 'New (will stage on Save)' : ciqStaged ? 'Staged (override)' : 'Reuse existing'}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 600 }}>Credentials</td><td style={cell}>{clearCreds ? 'Clear (reuse existing)' : newUser && newPass ? 'New (will save)' : credsSet ? `Set (user: ${credsUsername})` : 'Reuse existing'}</td></tr>
              <tr><td style={{ ...cell, fontWeight: 600 }}>Day-1 files</td><td style={cell}>{day1Files.length > 0 ? `${day1Files.length} staged (override)` : 'Reuse existing'}</td></tr>
            </tbody>
          </table>
        </ReactEDSWrapper>
      </ReactEDSWrapper>

      {day1Preview &&
        ReactDOM.createPortal(
          <div
            className={theme ? 'light-theme' : 'dark-theme'}
            style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
            onClick={() => setDay1Preview(null)}
          >
            <div
              style={{ background: theme ? '#FFFFFF' : '#181818', color: theme ? '#000000' : '#FFFFFF', padding: 16, width: '70vw', maxHeight: '80vh', borderRadius: 6, display: 'flex', flexDirection: 'column' }}
              onClick={(e) => e.stopPropagation()}
            >
              <h4 style={{ margin: '0 0 12px', flexShrink: 0 }}>{day1Preview.name}</h4>
              <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }}>
                <LineNumberedEditor value={day1Preview.content} readOnly rows={12} darkMode={!theme} />
              </div>
              <div style={{ marginTop: 12, flexShrink: 0 }}>
                <ReactEDSWrapper component={Button} primary onClick={() => setDay1Preview(null)}>Close</ReactEDSWrapper>
              </div>
            </div>
          </div>,
          document.body
        )}
      </ReactEDSWrapper>
    </div>,
    document.body
  );
}
